import React, { useState, useRef, useEffect } from 'react';
import {
  Badge, Button, Form, Modal, Table, Alert,
} from 'react-bootstrap';
import { Navbar } from '../../../components/Navbar';
import { Sidebar } from '../../../components/Sidebar';
import usePendenciasConfiguracoes from '../../../services/api/pendenciasConfiguracoes';

const emptyForm = {
  nome: '', descricao: '', ativo: true, ordem: 0, categoriaBeneficio: 'AMBOS', contexto: 'AMBOS',
};

const CATEGORIA_BENEFICIO_LABEL = {
  LOAS: 'LOAS',
  APOSENTADORIA: 'Aposentadoria',
  AMBOS: 'Ambos',
};

const CONTEXTO_LABEL = {
  ASSINOU: 'Documento pendente (assinou contrato)',
  NAO_ASSINOU: 'Motivo de não fechamento (não assinou)',
  AMBOS: 'Ambos',
};

const NENHUM_LABEL = 'Nenhum (não aparece no Atendimento)';

export default function PendenciasConfiguracoes() {
  const [submitting, setSubmitting] = useState(false);

  const normalizeMsg = (msg) => {
    if (!msg) return '';
    if (typeof msg === 'string') return msg;
    if (typeof msg?.message === 'string') return msg.message;
    if (typeof msg?.mensagem === 'string') return msg.mensagem;
    try { return JSON.stringify(msg); } catch { return String(msg); }
  };

  const [floatAlert, setFloatAlert] = useState({
    isVisible: false,
    message: '',
    variant: 'success',
  });

  const floatTimerRef = useRef(null);

  const showFloatAlert = (message, variant = 'success') => {
    if (floatTimerRef.current) clearTimeout(floatTimerRef.current);

    setFloatAlert({ isVisible: true, message: normalizeMsg(message), variant });
    floatTimerRef.current = setTimeout(() => {
      setFloatAlert({ isVisible: false, message: '', variant: 'success' });
      floatTimerRef.current = null;
    }, 3000);
  };

  useEffect(() => () => {
    if (floatTimerRef.current) clearTimeout(floatTimerRef.current);
  }, []);

  const [show, setShow] = useState(false);
  const [mode, setMode] = useState('create'); // create | edit
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  // Acesso já é gated na rota (routesAdm.jsx, requiresPermissao=
  // "configuracoes.pendencias") - não repetir a checagem aqui.

  const {
    pendencias,
    isLoading,
    isError,
    criarPendencia,
    editarPendencia,
  } = usePendenciasConfiguracoes();

  const isBusy = submitting || isLoading;

  const openCreate = () => {
    setMode('create');
    setEditingId(null);
    setForm(emptyForm);
    setShow(true);
  };

  const openEdit = (p) => {
    setMode('edit');
    setEditingId(p.id);
    setForm({
      nome: p.nome || '',
      descricao: p.descricao || '',
      ativo: !!p.ativo,
      ordem: Number.isFinite(p.ordem) ? p.ordem : 0,
      categoriaBeneficio: p.categoriaBeneficio || '',
      contexto: p.contexto || '',
    });
    setShow(true);
  };

  const validate = () => {
    if (!form.nome.trim()) return 'Informe o nome.';
    return null;
  };

  const renderTableBody = () => {
    if (isLoading) {
      return (
        <tr>
          <td colSpan={6} className="text-center text-muted py-4">
            Carregando...
          </td>
        </tr>
      );
    }

    if (isError) {
      return (
        <tr>
          <td colSpan={6} className="text-center text-danger py-4">
            Erro ao carregar pendências.
          </td>
        </tr>
      );
    }

    if ((pendencias || []).length === 0) {
      return (
        <tr>
          <td colSpan={6} className="text-center text-muted py-4">
            Nenhuma pendência cadastrada.
          </td>
        </tr>
      );
    }

    return pendencias.map((p) => (
      <tr key={p.id}>
        <td>{String(p.nome || '').toUpperCase()}</td>

        <td className="text-muted">{p.descricao || '-'}</td>

        <td>{CATEGORIA_BENEFICIO_LABEL[p.categoriaBeneficio] || NENHUM_LABEL}</td>

        <td>{CONTEXTO_LABEL[p.contexto] || NENHUM_LABEL}</td>

        <td className="text-center">
          <Badge bg={p.ativo ? 'success' : 'secondary'}>
            {p.ativo ? 'Ativo' : 'Inativo'}
          </Badge>
        </td>

        <td className="text-center">
          <div className="d-flex justify-content-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => openEdit(p)}
              disabled={isBusy}
            >
              Editar
            </Button>
          </div>
        </td>
      </tr>
    ));
  };

  const save = async () => {
    const err = validate();
    if (err) {
      showFloatAlert(err, 'danger');
      return;
    }

    const payload = {
      nome: form.nome.trim().toUpperCase(),
      descricao: form.descricao?.trim() || null,
      ativo: !!form.ativo,
      ordem: Number.isFinite(Number(form.ordem)) ? Number(form.ordem) : 0,
      categoriaBeneficio: form.categoriaBeneficio || null,
      contexto: form.contexto || null,
    };

    setSubmitting(true);
    try {
      if (mode === 'create') {
        await criarPendencia(payload);
      } else {
        await editarPendencia(editingId, payload);
      }

      setShow(false);
      setEditingId(null);
      setForm(emptyForm);
      showFloatAlert('Dados salvos com sucesso!', 'success');
    } catch (e) {
      console.error('Erro ao salvar pendência:', e);

      const status = e?.response?.status;
      const apiMsg = e?.response?.data?.mensagem
    || e?.response?.data?.message
    || e?.response?.data?.error;

      const msg = apiMsg
    || (status ? `Falha ao salvar (HTTP ${status}).` : null)
    || e?.message
    || 'Não foi possível salvar a pendência.';

      showFloatAlert(msg, 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {floatAlert.isVisible && (
      <div className="fixed-top mt-5" style={{ zIndex: 3000 }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-6">
              <Alert
                variant={floatAlert.variant}
                onClose={() => setFloatAlert({ isVisible: false, message: '', variant: 'success' })}
                dismissible
              >
                <p className="text-center mt-3">{floatAlert.message}</p>
              </Alert>
            </div>
          </div>
        </div>
      </div>
      )}

      <div id="wrapper">
        <Sidebar />

        <div id="content-wrapper" className="d-flex flex-column">
          <div id="content">
            <Navbar />

            <div className="container-fluid">
              <div className="d-sm-flex align-items-center justify-content-between mb-4">
                <h1 className="h3 mb-0 text-gray-800">Pendências - Configurações</h1>

                <Button variant="success" onClick={openCreate} disabled={isBusy}>
                  Criar nova pendência
                </Button>
              </div>

              <hr />

              <div className="card shadow mb-4">
                <div className="card-header py-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <strong className="m-0">Lista de Pendências</strong>
                </div>

                <div className="card-body">
                  <Table responsive size="sm" className="mb-0 align-middle">
                    <thead>
                      <tr>
                        <th style={{ width: '30%' }}>Nome</th>
                        <th>Descrição</th>
                        <th>Benefício</th>
                        <th>Contexto</th>
                        <th style={{ width: '14%' }} className="text-center">
                          Ativo
                        </th>
                        <th style={{ width: '16%' }} className="text-center">
                          Ações
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {renderTableBody()}
                    </tbody>
                  </Table>
                </div>
              </div>

              <Modal
                show={show}
                onHide={() => {
                  if (isBusy) return;
                  setShow(false);
                  setEditingId(null);
                  setForm(emptyForm);
                }}
                centered
              >
                <Modal.Header closeButton={!isBusy}>
                  <Modal.Title>
                    {mode === 'create' ? 'Criar nova pendência' : 'Editar pendência'}
                  </Modal.Title>
                </Modal.Header>

                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (isBusy) return;
                    save();
                  }}
                >
                  <Modal.Body>
                    <div className="mb-3">
                      <Form.Label className="fw-semibold">Nome:</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Ex: CERTIDÃO DE CASAMENTO"
                        value={form.nome}
                        onChange={(e) => setForm((prev) => ({
                          ...prev,
                          nome: e.target.value.toUpperCase(),
                        }))}
                        maxLength={80}
                        disabled={isBusy}
                      />
                    </div>

                    <div className="mb-3">
                      <Form.Label className="fw-semibold">Descrição:</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Ex: LOAS"
                        value={form.descricao}
                        onChange={(e) => setForm((prev) => ({
                          ...prev,
                          descricao: e.target.value,
                        }))}
                        maxLength={200}
                        disabled={isBusy}
                      />
                    </div>

                    <Form.Group className="mb-3" controlId="pendencia-categoria-beneficio">
                      <Form.Label className="fw-semibold">Aparece para o benefício:</Form.Label>
                      <Form.Select
                        value={form.categoriaBeneficio}
                        onChange={(e) => setForm((prev) => ({
                          ...prev,
                          categoriaBeneficio: e.target.value,
                        }))}
                        disabled={isBusy}
                      >
                        {Object.entries(CATEGORIA_BENEFICIO_LABEL).map(([valor, label]) => (
                          <option key={valor} value={valor}>{label}</option>
                        ))}
                        <option value="">{NENHUM_LABEL}</option>
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="pendencia-contexto">
                      <Form.Label className="fw-semibold">Aparece quando o cliente:</Form.Label>
                      <Form.Select
                        value={form.contexto}
                        onChange={(e) => setForm((prev) => ({
                          ...prev,
                          contexto: e.target.value,
                        }))}
                        disabled={isBusy}
                      >
                        {Object.entries(CONTEXTO_LABEL).map(([valor, label]) => (
                          <option key={valor} value={valor}>{label}</option>
                        ))}
                        <option value="">{NENHUM_LABEL}</option>
                      </Form.Select>
                    </Form.Group>

                    {mode === 'edit' && (
                      <div className="mb-3">
                        <Form.Check
                          type="switch"
                          id="pendencia-ativo-switch"
                          label={form.ativo ? 'Ativo (aparece pra uso)' : 'Inativo (some do uso, histórico é preservado)'}
                          checked={form.ativo}
                          onChange={(e) => setForm((prev) => ({
                            ...prev,
                            ativo: e.target.checked,
                          }))}
                          disabled={isBusy}
                        />
                      </div>
                    )}
                  </Modal.Body>

                  <Modal.Footer>
                    <Button
                      variant="outline-secondary"
                      type="button"
                      onClick={() => {
                        if (isBusy) return;
                        setShow(false);
                        setEditingId(null);
                        setForm(emptyForm);
                      }}
                      disabled={isBusy}
                    >
                      Cancelar
                    </Button>

                    <Button variant="primary" type="submit" disabled={isBusy}>
                      {submitting ? 'Salvando...' : 'Salvar'}
                    </Button>
                  </Modal.Footer>
                </Form>
              </Modal>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
